import RfiElement from './RfiElement';

export type ElementType = "field" | "survey";
export const elementTypes = {
  FIELD: "field" as ElementType,
  SURVEY: "survey" as ElementType,
};

export type FieldType =
  | "checkbox"
  | "radiogroup"
  | "dropdown"
  | "sectionheader"
  | "guidancetext"
  | "textfield"
  | "datetime"
  | "textarea"
  | "richtext"
  | "attachment"
  | "lookup"
  | "lookupmultiple"
  | "select"
  | "selectmultiple";

export const fieldTypes = {
  CHECKBOX: "checkbox" as FieldType,
  RADIOGROUP: "radiogroup" as FieldType,
  DROPDOWN: "dropdown" as FieldType,
  SECTIONHEADER: "sectionheader" as FieldType,
  GUIDANCETEXT: "guidancetext" as FieldType,
  TEXTFIELD: "textfield" as FieldType,
  DATETIME: "datetime" as FieldType,
  TEXTAREA: "textarea" as FieldType,
  RICHTEXT: "richtext" as FieldType,
  ATTACHMENT: "attachment" as FieldType,
  LOOKUP: "lookup" as FieldType,
  LOOKUPMULTIPLE: "lookupmultiple" as FieldType,
  DATEPICKER: "datepicker" as FieldType,
  TIMEPICKER: "timepicker" as FieldType,
  SELECT: "select" as FieldType,
  SELECTMULTIPLE: "selectmultiple" as FieldType,
};

export type ElementOptionType = "default" | "other";
export const optionTypes = {
  DEFAULT: "default" as ElementOptionType,
  OTHER: "other" as ElementOptionType,
};

export type AnswerValueType =
  | "default"
  | "other"
  | "option"
  | "richtext"
  | "attachment";
export const answerValueTypes = {
  DEFAULT: "default" as AnswerValueType,
  OTHER: "other" as AnswerValueType,
  OPTION: "option" as AnswerValueType,
  RICH_TEXT: "richtext" as AnswerValueType,
  ATTACHMENT: "attachment" as AnswerValueType,
};

export interface SectionGroup {
  sectionGroupId?: number;
  sectionGroupName?: string;
  sequence?: number;
  questionNumber?: number;
  renderType?: string;
}
export interface Element {
  elementId: number;
  rfiElementLink?: string;
  previousElementId?: string;
  isDirty?: boolean | undefined;
  sections?: SectionGroup[] | undefined | null;
  customerId?: number;
  elementType: string;
  fieldType: string;
  fieldLabel: string;
  editLabel?: boolean | undefined;
  lookupCode?: string;
  customerRequired?: boolean | undefined;
  requiredInRfi?: boolean | undefined;
  requiredResponse?: boolean | undefined;
  editRequired?: boolean | undefined;
  rfiId?: number;
  mapsToField?: string;
  dateCreated?: string;
  dateLastEdited?: string;
  lastUpdatedUserId?: number;
  options?: ElementOption[] | undefined | null | [];
  answers?: ElementAnswer[] | undefined | null | [];
  vendorQuestions?: ElementVendorQuestion[] | undefined | null | [];
}

export interface ElementOption {
  value: string | undefined;
  description: string | null;
  sequence: number;
  type?: ElementOptionType;
}

export interface ElementAnswer {
  id?: number;
  value?: string;
  vendorResponseId?: number;
  answerValueType?: string;
  attachment?: Attachment;
  displayText?: string;
}

export interface ElementValue {
  value: string;
  answerValueType: string;
}

export interface LibraryElementQuestionCategory {
  status: string;
  categoryName: string;
}

export interface IElementProps {
  element: RfiElement;
  readOnly?: boolean;
  questionNumber?: number;
  showValue?: boolean;
  designMode?: boolean;
  isMulti?: boolean;
  endpoint?: string;
  rerender?: boolean;
  defaultValue?: string;
  overrideRequired?: boolean;
}

export type RfiElementSelectionList = {
  elementId: number;
  fieldLabel: string;
};

export interface Attachment {
  id: number;
  title?: string;
  Description?: string;
  filename?: string;
  attachmentLink?: string;
  rfxAttachmentId?: number;
  rfiId?: number;
  sequence?: number;
}

export enum AttachmentType {
  RFI = "rfiattachment",
  VENDOR_RESPONSE = "responseattachment",
  ELEMENT = "elementattachment",
}

export enum RfiStatus {
  IN_PROGRESS = "In Progress",
  POSTING_FAILED = "Posting Failed",
  POSTED = "Posted",
  CANCELLED = "Cancelled",
}

export type ElementMode = "edit" | "copy" | "move" | undefined;
export interface ElementVendorQuestion {
  rfxVendorQuestionId: number;
  versionNumber: number;
  questionText?: string;
  answerText?: string;
  answerStatus?: string;
  voteCount: number;
  isMine?: string;
  didIVote?: string;
  section?: string;
  questionNumber?: number;
  fieldLabel?: string;
}

export interface MappedToField {
  fieldName: string;
  value?: string;
  fieldType?: string;
}

export const sectionNames = {
  DESCRIBE: "Describe",
  VENDOR_INFO: "Vendor Information",
  QUESTIONNAIRE: "Questionnaire",
  ATTACHMENTS: "Attchments",
  FINALIZE: "Finalize",
};

export interface PageState {
  skip: number;
  take: number;
}

export interface GetRfiDataOptions {
  countOnly?: boolean;
  includeElements?: boolean;
  activeRfis?: boolean;
  myRfis?: boolean;
  includeMappedTo?: boolean;
}

export interface IRfiListTableProps {
  height: number;
  filters?: any;
  page: number;
  setPage: any;
  options: GetRfiDataOptions;
  title: string;
}

export interface IRfiVersion {
  rfiId: number;
  rfiNumber: string;
  versionNumber: number;
  status: string;
  customerName: string;
  actionDate: Date;
}

export interface VendorResponseDto {
  vendorResponseId: number;
  dateSubmitted?: Date;
  attachments?: Attachment[];
  vendorName: string;
  uei?: string;
}

export interface VendorResponse {
  vendorResponseId: number;
  dateSubmitted?: Date;
  dateCreated?: Date;
  dateLastEdited?: Date;
  previousVendorResponseId?: number;
  vendorName?: string;
  vendorResponseLink?: string;
  uei?: string;
  vendorUserId?: number;
  attachments?: Attachment[];
  email?: string;
  answers?: ElementAnswer[];
}

export interface ResponseSummary {
  elementId?: number;
  questionNumber?: number;
  elementLabel?: string;
  elementType?: string;
  elementFieldType?: string;
  answers?: SummaryAnswer[];
  sectionGroupName?: string;
}

export interface SummaryAnswer {
  elementId?: number;
  optionDescription?: string;
  optionTotal?: number;
  optionType?: string;
  total?: number;
  lastResponse?: string;
  color?: string;
}

export interface DataResponse<T> {
  total: number;
  records: T;
}

export interface RfiData {
  rfiId: number;
  title: string;
  rfiNumber: string;
  versionNumber: number;
  status: string;
  postedToSamDate?: string;
  attachments?: Attachment[];
  vendorQuestions?: ElementVendorQuestion[];
  mappedFields?: MappedToField[];
  isOwner?: number;
  isCreator?: number;
  isEditor?: number;
  isLocked?: number;
  createdByName?: string;
  lockedById?: number;
  lockedByName?: string;
  postedToSamBy?: number;
  postedToSamName?: string;
  previousRfiId?: number;
  qaCloseDate?: string | null;
  responsesDueDate?: string | null;
  samOpportunityId?: string | null;
  samError?: string | null;
  versions?: IRfiVersion[] | null;
  ownedById?: number;
  ownedByName?: string | null;
  dateCreated?: Date | null;
  archiveDate?: Date | null;
  vendorResponseId?: number;
  vendorResponseCount?: number | null;
  rfiLink?: string;
  postedByName?: string;
  category?: string;
  cancelledOpportunityId?: string;
  cancelledReason?: string;
  cancelledDescription?: string;
  timeZone?: string;
  createdById?: number;
  cancelledDate?: Date;
  alertDays?: number;
  elements: Element[];
}

export interface RfiHistory {
  records?: RfiHistoryRecord[];
  editors?: RfiEditor[];
  dateCreated?: Date;
  ownerName?: string;
  ownerAssignedDate?: Date;
  ownerAssignedByName?: string;
}

export interface RfiHistoryRecord {
  rfiId?: number;
  previousRfiId?: number;
  rfiLink?: string;
  versionNumber?: number;
  customerName?: string;
  action?: string;
  actionDateTime?: Date;
  rfiTitle?: string;
  status?: string;
}

export interface RfiEditor {
  name?: string;
  dateLastEdited?: Date;
}
